import { type RefObject, useCallback, useState } from 'react';

import { showToast } from '../../utils/helper';

// jspdf + html2canvas are heavy, so they live behind dynamic imports and land in
// the `pdf-gen` chunk (see vite.config.ts manualChunks). They are only fetched
// the first time a volunteer taps "Download certificate" — mirroring the
// media-conv lazy pattern in utils/media/normaliseFile.ts.
const html2canvasOnce = async () => {
	const mod = await import('html2canvas');
	return mod.default;
};

const jsPdfOnce = async () => {
	const mod = await import('jspdf');
	return mod.jsPDF;
};

interface UseCertificateDownload {
	downloading: boolean;
	downloadCertificate: () => Promise<void>;
}

/**
 * Rasterises the off-screen certificate node to a PNG and wraps it in an
 * A4-landscape PDF that downloads directly. The node is authored at a fixed A4
 * landscape aspect ratio so the image fills the page with no distortion.
 */
export const useCertificateDownload = (nodeRef: RefObject<HTMLDivElement | null>, fileName: string): UseCertificateDownload => {
	const [downloading, setDownloading] = useState(false);

	const downloadCertificate = useCallback(async () => {
		const node = nodeRef.current;
		if (!node || downloading) return;

		setDownloading(true);
		try {
			const [html2canvas, JsPDF] = await Promise.all([html2canvasOnce(), jsPdfOnce()]);

			const canvas = await html2canvas(node, {
				scale: 2,
				backgroundColor: '#f7f4ec',
				useCORS: true,
				logging: false,
			});

			const pdf = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();
			const imgHeight = (canvas.height * pageWidth) / canvas.width;
			const drawHeight = Math.min(imgHeight, pageHeight);
			const offsetY = drawHeight < pageHeight ? (pageHeight - drawHeight) / 2 : 0;

			pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, offsetY, pageWidth, drawHeight);
			pdf.save(`${fileName}.pdf`);
		} catch {
			showToast('error', 'We couldn’t generate your certificate. Please try again.');
		} finally {
			setDownloading(false);
		}
	}, [nodeRef, downloading, fileName]);

	return { downloading, downloadCertificate };
};
